import type { ComponentProps } from '@enonic/react-components'
import * as React from 'react'
import { Section, Container } from '../primitives'
import { Icon } from '/react4xp/components/primitives/Icon'

const TYPE_LABELS: Record<string, string> = {
  education: 'Utdanning',
  'education-landing': 'Utdanning',
  'info-page': 'Informasjon',
  news: 'Nyhet',
  'news-landing': 'Nyheter',
  person: 'Person',
  'person-landing': 'Ansatte',
}


interface SearchResultItem {
  id: string
  title: string
  description: string
  url: string
  contentType: string
}

export interface SearchData extends Record<string, unknown> {
  title: string
  query: string
  results: SearchResultItem[]
  total: number
}

export const Search = (props: ComponentProps) => {
  const data = props.data as SearchData
  const { title, query: searchQuery, results, total } = data

  return (
    <>
      <Section padding="none">
        <Container className="gap-y-10 pt-10 pb-6 md:gap-y-20 md:pt-20 md:pb-10">
          <div className="col-span-full md:col-span-8">
            <h1 className="font-serif text-4xl leading-tightest text-blue-900 md:text-7xl">
              {title}
            </h1>
          </div>

          <div className="col-span-full flex flex-col gap-4 md:col-span-8">
            <form action="" method="get" role="search">
              <label htmlFor="search-input" className="sr-only">
                Søk
              </label>
              <div className="flex min-w-0 items-center gap-1 rounded-full bg-slate-100 py-1.5 pl-2 pr-2 has-[input:focus-visible]:outline-1 has-[input:focus-visible]:outline-slate-300">
                <button
                  type="submit"
                  aria-label="Søk"
                  className="flex shrink-0 items-center rounded-full text-slate-700 hover:text-black focus-visible:outline-1 focus-visible:outline-slate-300"
                >
                  <Icon name="search" className="md:text-3xl!" />
                </button>
                <input
                  id="search-input"
                  type="search"
                  name="q"
                  defaultValue={searchQuery}
                  placeholder="Søk..."
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent text-base text-black placeholder:text-slate-700 focus:outline-none md:text-xl"
                />
                {searchQuery && (
                  <a
                    href="?"
                    className="flex shrink-0 cursor-pointer items-center rounded-full text-slate-700 hover:text-black focus-visible:outline-1 focus-visible:outline-slate-300"
                    aria-label="Tøm søk"
                  >
                    <Icon name="close" className="md:text-3xl!" />
                  </a>
                )}
              </div>
            </form>

            {searchQuery && (
              <p className="text-base text-black" role="status" aria-live="polite">
                Vi fant {total} {total === 1 ? 'resultat' : 'resultater'} for{' '}
                <strong>&quot;{searchQuery}&quot;</strong>
              </p>
            )}
          </div>
        </Container>
      </Section>

      <Section background="bg-slate-100">
        <Container>
          <div className="col-span-full flex flex-col md:col-span-8">
            {results.length > 0 ? (
              <ul className="flex flex-col">
                {results.map((result) => (
                  <li
                    key={result.id}
                    className="border-t border-slate-300 py-10"
                  >
                    {TYPE_LABELS[result.contentType] && (
                      <span className="text-sm font-medium uppercase tracking-wider text-slate-500">
                        {TYPE_LABELS[result.contentType]}
                      </span>
                    )}
                    <a
                      href={result.url}
                      className="mt-1 block text-2xl leading-relaxed text-slate-900 underline decoration-blue-500 decoration-2 underline-offset-4 hover:text-black"
                    >
                      {result.title}
                    </a>
                    {result.description && (
                      <p className="mt-4 text-xl leading-normal text-slate-900">
                        {result.description}
                      </p>
                    )}
                  </li>
                ))}
                <li className="border-t border-slate-300" aria-hidden="true" />
              </ul>
            ) : searchQuery ? (
              <p className="text-xl text-slate-900">
                Ingen resultater funnet for &quot;{searchQuery}&quot;.
              </p>
            ) : null}
          </div>
        </Container>
      </Section>
    </>
  )
}
